/**
 * UpdateNotifier.tsx — 新バージョン通知バナー（2026-07-26）
 *
 * デプロイで version.json が更新されたことを検知し、開きっぱなしのブラウザに
 * 「更新があります」バナーを表示する。運用上タブを開きっぱなしにするため、
 * デプロイ後も旧バンドルで動き続ける問題（Pusherキー欠落事故の温床にもなった）への対策。
 *
 * 仕組み: ページロード時に /version.json を取得して基準値とし、5分ごと+タブ復帰時に
 * 再取得。バージョンが変わったらバナー表示（「更新」でリロード）。
 */
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { BUILD_VERSION } from '../lib/buildVersion';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5分

const UpdateNotifier: React.FC = () => {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    // 基準 = このバンドルのバージョン（焼き込み）。無ければ最初に取れた version.json
    const baseVersionRef = useRef<string | null>(BUILD_VERSION);

    useEffect(() => {
        let cancelled = false;
        const check = async () => {
            try {
                const res = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' });
                if (!res.ok) return;
                const j = await res.json();
                const v = typeof j?.version === 'string' ? j.version : null;
                if (!v || cancelled) return;
                if (baseVersionRef.current === null) {
                    baseVersionRef.current = v; // 初回 = このタブが動いている（とみなす）バージョン
                    return;
                }
                if (v !== baseVersionRef.current) setUpdateAvailable(true);
            } catch { /* オフライン等は無視 */ }
        };
        check();
        const id = setInterval(check, CHECK_INTERVAL_MS);
        const onVis = () => { if (document.visibilityState === 'visible') check(); };
        document.addEventListener('visibilitychange', onVis);
        return () => { cancelled = true; clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
    }, []);

    if (!updateAvailable) return null;

    return (
        <Box sx={{
            position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 3000,
            display: 'flex', alignItems: 'center', gap: 1.5,
            bgcolor: '#1e293b', color: '#fff', px: 2, py: 1, borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
        }}>
            <SystemUpdateAltIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                更新があります — 新しいバージョンが利用可能です
            </Typography>
            <Button
                size="small" variant="contained" color="primary"
                onClick={() => window.location.reload()}
                sx={{ fontWeight: 700, px: 2 }}
            >
                更新
            </Button>
        </Box>
    );
};

export default UpdateNotifier;
